/**
 * Grayscale raster + global thresholding.
 *
 * Everything in `lib/trace` is deliberately DOM-free: the browser hands us the
 * `Uint8ClampedArray` it got out of `CanvasRenderingContext2D.getImageData()`
 * and from there on the pipeline is arithmetic only. That is what lets the
 * whole tracing library run under the repo's vitest harness (`environment:
 * 'node'`), where no canvas, no `ImageData` and no `OffscreenCanvas` exist.
 */

/** One byte per pixel, row-major, `data[y * width + x]`, 0 = black … 255 = white. */
export interface GrayRaster {
  width: number
  height: number
  data: Uint8ClampedArray
}

/**
 * Rec. 709 luma weights (sRGB). The scans are lit sheet material on a flat
 * scanner bed, so any sane luma works; 709 matches the sRGB primaries the
 * scanner driver writes, which keeps a coloured leather piece from reading
 * darker than its actual lightness.
 *
 * Sums to 1 in floating point by construction of the constants below.
 */
const LUMA_R = 0.2126
const LUMA_G = 0.7152
const LUMA_B = 0.0722

/** Threshold returned when a raster carries no information to split on. */
export const NEUTRAL_THRESHOLD = 127

/**
 * Flattens RGBA to a single grey byte per pixel.
 *
 * The alpha channel is ignored on purpose: scanner output is opaque, and a
 * canvas that was never painted reads as `rgba(0,0,0,0)`, i.e. luma 0, which is
 * exactly the "background" end of the bright-on-dark convention this module
 * uses. Compositing over white instead would turn unpainted canvas into
 * foreground.
 *
 * @throws RangeError when the dimensions are not positive integers, or when
 *   `rgba` is shorter than `width * height * 4`.
 */
export function rgbaToGray(width: number, height: number, rgba: Uint8ClampedArray): GrayRaster {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) {
    throw new RangeError(`rgbaToGray: width/height must be positive integers, got ${width}x${height}`)
  }
  const pixels = width * height
  if (rgba.length < pixels * 4) {
    throw new RangeError(`rgbaToGray: expected at least ${pixels * 4} bytes, got ${rgba.length}`)
  }

  const data = new Uint8ClampedArray(pixels)
  for (let i = 0; i < pixels; i += 1) {
    const o = i * 4
    // Uint8ClampedArray rounds on store, so the fractional luma lands on the
    // nearest byte without an explicit Math.round.
    data[i] = LUMA_R * rgba[o]! + LUMA_G * rgba[o + 1]! + LUMA_B * rgba[o + 2]!
  }
  return { width, height, data }
}

/** 256-bin intensity histogram of a raster. */
export function histogram(r: GrayRaster): Int32Array {
  const bins = new Int32Array(256)
  const { data } = r
  const pixels = r.width * r.height
  for (let i = 0; i < pixels; i += 1) {
    const v = data[i]!
    bins[v] = bins[v]! + 1
  }
  return bins
}

/**
 * Otsu's method on a 256-bin histogram.
 *
 * Returns the bin `t` that maximises between-class variance for the split
 * `background = [0…t]`, `foreground = [t+1…255]`. Callers therefore classify a
 * pixel as foreground with **`value > threshold`** — the same comparison
 * `traceShapes` uses.
 *
 * A raster with a single intensity (blank scan, all-black frame) has no split
 * that separates anything; there the function returns {@link NEUTRAL_THRESHOLD}
 * rather than an arbitrary bin, so a blank scan yields zero shapes instead of
 * one shape covering the whole bed.
 *
 * Single pass over the histogram: running class weights and first moments give
 * between-class variance as `(mTotal·w0 − m0)² / (w0·(n − w0))`, which avoids
 * dividing twice per bin.
 */
export function otsuThreshold(r: GrayRaster): number {
  const total = r.width * r.height
  if (total <= 0) return NEUTRAL_THRESHOLD

  const bins = histogram(r)

  let sumAll = 0
  for (let v = 0; v < 256; v += 1) sumAll += v * bins[v]!

  let w0 = 0
  let sum0 = 0
  let best = -1
  let bestVariance = -1
  for (let t = 0; t < 255; t += 1) {
    w0 += bins[t]!
    if (w0 === 0) continue
    const w1 = total - w0
    if (w1 === 0) break
    sum0 += t * bins[t]!
    // Between-class variance, scaled by total² (monotone, so argmax is intact).
    const between = (sumAll * w0 - sum0 * total) ** 2 / (w0 * w1)
    if (between > bestVariance) {
      bestVariance = between
      best = t
    }
  }

  return best < 0 ? NEUTRAL_THRESHOLD : best
}

/**
 * Binarises a raster into a foreground mask, bright-on-dark:
 * `mask[i] = 1` when `data[i] > threshold`.
 *
 * Uint8Array (not a bitset) because every later pass — flood fill, erosion,
 * boundary tracing — does random access, and byte reads beat bit twiddling by
 * more than the 8× memory costs on a 4 MP scan.
 */
export function binarize(r: GrayRaster, threshold: number): Uint8Array {
  const pixels = r.width * r.height
  const mask = new Uint8Array(pixels)
  const { data } = r
  for (let i = 0; i < pixels; i += 1) mask[i] = data[i]! > threshold ? 1 : 0
  return mask
}
